import { Box, Flex, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useLang } from "../../../../Hooks";
import { useAutoTableHeight } from "../../useAutoTableHeight";
import { discoverReportKeys } from "../Helpers/exportHelpers";

const formatNumber = (value) => {
  if (typeof value !== "number") return value || "-";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const TrialBalanceTable = ({ data }) => {
  const { texts } = useLang();
  const isRTL = texts.en === "Arabic";
  const tableHeight = useAutoTableHeight(150);
  const columns = useMemo(() => discoverReportKeys(data), [data]);

  if (!data?.rows) {
    return <Text p={4}>No data to display.</Text>;
  }

  const renderCells = (row, level, isSummary = false) =>
    columns.map((column, index) => {
      const source = isSummary ? row.sub_totals || row.totals || row : row;
      const rawValue =
        column.key === "label"
          ? source.label || row.name || "-"
          : source[column.key] ?? row[column.key] ?? "-";

      return (
        <td
          key={`${row.id || row.code || column.key}-${column.key}`}
          style={{
            padding: "5px 6px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            textAlign:
              column.key === "label"
                ? isRTL
                  ? "right"
                  : "left"
                : isRTL
                ? "left"
                : "right",
            borderInlineEnd: index === 0 ? "1px solid #eaeef2" : "none",
            color:
              typeof rawValue === "number" && rawValue < 0 ? "#dc3545" : "#333333",
            paddingInlineStart:
              column.key === "label" && !isSummary ? `${6 + level * 10}px` : "6px",
          }}
        >
          {column.align === "right" ? formatNumber(rawValue) : rawValue}
        </td>
      );
    });

  const renderRows = (rows = [], level = 0) =>
    rows.flatMap((row) => {
      const isGroup = row.group && row.id === row.group && !row.code;
      const isAccount = row.code && row.name;
      const isSummary = row.summary && (row.summary.sub_totals || row.summary.totals);
      const currentRow =
        isGroup || isSummary || isAccount ? (
          <tr
            key={row.id || row.code || `${row.label}-${level}`}
            style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #eaeef2" }}
          >
            {renderCells(isSummary ? row.summary : row, level, isSummary)}
          </tr>
        ) : null;

      return [currentRow, ...renderRows(row.children, level + 1)].filter(Boolean);
    });

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="sm"
      border="1px solid"
      borderColor="#eaeef2"
      height={`${tableHeight}px`}
      position="relative"
    >
      <Box position="absolute" inset="0" overflowY="auto" overflowX="auto">
        <table
          style={{
            width: "100%",
            minWidth: "700px",
            borderCollapse: "collapse",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <thead>
            <tr style={{ position: "sticky", top: 0, background: "#f5f7fa", zIndex: 2 }}>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  style={{
                    padding: "6px",
                    fontSize: "12px",
                    textTransform: "uppercase",
                    textAlign:
                      column.key === "label"
                        ? isRTL
                          ? "right"
                          : "left"
                        : isRTL
                        ? "left"
                        : "right",
                    minWidth: column.key === "label" ? "400px" : "120px",
                    borderInlineEnd: index === 0 ? "1px solid #dce1e6" : "none",
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderRows(data.rows)}
            {data.summary?.totals && (
              <tr style={{ backgroundColor: "#f5f7fa", borderTop: "2px solid #dce1e6" }}>
                {renderCells(data.summary, 0, true)}
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};
